// Project-calendar helpers. Meter data is read on the PROJECT's calendar (its
// IANA zone, `project_timezone` on each /me group — the `application` rule:
// a day is the project's local day, whoever looks).
//
// The period code keeps calendar days as "wall dates": Dates whose UTC fields
// hold the project's local wall clock (2026-09-27 local = 2026-09-27T00:00Z).
// All calendar math (anchors, pickers, titles) stays in that space; these
// helpers convert only at the edges — wall → instant for the API, instant →
// wall for what comes back. Ported 1:1 to the mobile app's lib/zone.ts.

// Zone for groups from a portal-api that predates project_timezone: its
// buckets are UTC days, so the client must read them as UTC too.
export const LEGACY_ZONE = 'UTC';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(tz, f);
  }
  return f;
}

// toWall returns the wall date of `instant` in tz: a Date whose UTC fields
// read the local date and time there.
export function toWall(instant: Date, tz: string): Date {
  if (tz === 'UTC') return new Date(instant.getTime());
  const p: Record<string, number> = {};
  for (const part of formatter(tz).formatToParts(instant)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  return new Date(
    Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, instant.getUTCMilliseconds()),
  );
}

// fromWall is the inverse: the instant at which tz's clock reads `wall`. A
// wall time that a DST change skips or repeats resolves to an adjacent
// instant; midnights, the only wall times periods start on, are unaffected
// in Europe, where clocks change at 01:00 UTC.
export function fromWall(wall: Date, tz: string): Date {
  if (tz === 'UTC') return new Date(wall.getTime());
  const guess = wall.getTime();
  const offset = (t: number) => toWall(new Date(t), tz).getTime() - t;
  const o1 = offset(guess - offset(guess));
  const t1 = guess - o1;
  if (offset(t1) === o1) return new Date(t1);
  return new Date(guess - offset(t1));
}

// wallNow is "now" on the project calendar.
export function wallNow(tz: string): Date {
  return toWall(new Date(), tz);
}

// wallOf parses an API timestamp into its wall date in tz.
export function wallOf(iso: string, tz: string): Date {
  return toWall(new Date(iso), tz);
}
